import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole, useIsAdmin } from '@/hooks/use-user-role';

export const useTransportAccess = () => {
  const { isSiteAdmin } = useIsAdmin();
  const { data: role } = useUserRole();

  const { data: transportAdminData, isLoading } = useQuery({
    queryKey: ['transport-admin-check'],
    queryFn: async () => {
      // Check if user is a state transport admin
      const { data: isAdmin, error } = await supabase.rpc('is_state_transport_admin');
      if (error) {
        console.error('Error checking transport admin status:', error);
        return { isTransportAdmin: false, assignedStates: [] as string[], hasNationalAccess: false };
      }
      
      if (isAdmin) {
        // Get assigned states
        const { data: assignments } = await supabase
          .from('state_transport_admins')
          .select('state_code')
          .eq('is_active', true);
        
        const states = assignments?.map(a => a.state_code) || [];
        const hasNationalAccess = states.includes('USA');
        
        return {
          isTransportAdmin: true,
          assignedStates: hasNationalAccess ? [] : states.filter(s => s !== 'USA'),
          hasNationalAccess,
        };
      }
      
      return { isTransportAdmin: false, assignedStates: [] as string[], hasNationalAccess: false };
    },
    enabled: role === 'state_transport_admin',
    staleTime: 5 * 60 * 1000,
  });

  const isTransportAdmin = transportAdminData?.isTransportAdmin || false;
  const assignedStates = transportAdminData?.assignedStates || [];
  const hasNationalAccess = transportAdminData?.hasNationalAccess || false;

  return {
    canViewTransportNav: isSiteAdmin || isTransportAdmin,
    canManageTransporters: isSiteAdmin || isTransportAdmin,
    isSiteAdmin,
    isTransportAdmin,
    assignedStates,
    hasNationalAccess,
    isLoading,
  };
};
